import { createAsyncThunk } from '@reduxjs/toolkit';
import { loginApiClient, userApiClient } from '@/core/infrastructure/api/apiClient';
import { parseJwt } from '@/core/utils/jwtUtils';
import { translate } from '@/core/utils/translate';

type DecodedToken = {
  username?: string;
  preferred_username?: string;
  email?: string;
  firstName?: string;
  given_name?: string;
  lastName?: string;
  family_name?: string;
  role?: string | string[];
};

type TokenResponse = {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
};

export type AuthLoginResult = {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  user: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
  };
};

export type GoogleLoginError = {
  code:
    | 'GoogleCredentialMissing'
    | 'InvalidToken'
    | 'TokenMissing'
    | 'ServiceUnavailable'
    | 'GoogleLoginFailed';
  description?: string;
};

const getLanguage = () => (localStorage.getItem('i18nextLng') === 'tr' ? 'tr-TR' : 'en-US');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeRoles = (roles: DecodedToken['role']) => {
  if (Array.isArray(roles)) {
    return roles;
  }

  return roles ? [roles] : [];
};

const mapTokenResponse = (data: TokenResponse): AuthLoginResult | null => {
  if (!data.access_token || !data.refresh_token) {
    return null;
  }

  const decodedToken = parseJwt(data.access_token) as DecodedToken | null;

  if (!decodedToken) {
    return null;
  }

  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id_token: data.id_token,
    user: {
      username:
        decodedToken.username || decodedToken.preferred_username || decodedToken.email || '',
      email: decodedToken.email || '',
      firstName: decodedToken.firstName || decodedToken.given_name || '',
      lastName: decodedToken.lastName || decodedToken.family_name || '',
      roles: normalizeRoles(decodedToken.role),
    },
  };
};

const getGoogleLoginError = (error: unknown): GoogleLoginError => {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null;
  const data = response && isRecord(response.data) ? response.data : null;
  const description =
    typeof data?.error_description === 'string' ? data.error_description.trim() : '';

  if (response?.status === 503) {
    return { code: 'ServiceUnavailable' };
  }

  return {
    code: 'GoogleLoginFailed',
    ...(description ? { description } : {}),
  };
};

export const googleLoginUser = createAsyncThunk<
  AuthLoginResult,
  { credential: string },
  { rejectValue: GoogleLoginError }
>('auth/googleLoginUser', async ({ credential }, { rejectWithValue }) => {
  const normalizedCredential = credential.trim();

  if (!normalizedCredential) {
    return rejectWithValue({ code: 'GoogleCredentialMissing' });
  }

  const lang = getLanguage();

  try {
    const params = new URLSearchParams({
      grant_type: 'google_id_token',
      google_id_token: normalizedCredential,
      client_id: 'merzigogateway',
      client_secret: 'merzigogateway_secret',
      scope: 'openid profile email',
      'Accept-Language': lang,
    });

    const { data } = await loginApiClient.post<TokenResponse>('', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept-Language': lang,
      },
    });

    const result = mapTokenResponse(data);

    if (!data.access_token || !data.refresh_token) {
      return rejectWithValue({ code: 'TokenMissing' });
    }

    if (!result) {
      return rejectWithValue({ code: 'InvalidToken' });
    }

    return result;
  } catch (error: unknown) {
    return rejectWithValue(getGoogleLoginError(error));
  }
});

// Redux Thunk ile login işlemi
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const lang = getLanguage();

      const params = new URLSearchParams({
        grant_type: 'password',
        client_id: 'merzigogateway',
        client_secret: 'merzigogateway_secret',
        username: email,
        password,
        scope: 'openid profile email',
        'Accept-Language': lang,
      });

      const { data }: any = await loginApiClient.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept-Language': lang,
        },
      });

      const decodedToken = parseJwt(data.access_token);
      if (!decodedToken) {
        return rejectWithValue(translate('errors/api:token_invalid'));
      }

      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        id_token: data.id_token,
        user: {
          username: decodedToken.username,
          email: decodedToken.email,
          firstName: decodedToken.firstName,
          lastName: decodedToken.lastName,
          roles: decodedToken.role || [],
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || translate('errors/api:login_failed'));
    }
  }
);

// export const generateOtp = async (email: string) => {
//   if (!email || !email.trim()) {
//     throw new Error('Email zorunludur.');
//   }

//   try {
//     const lang = localStorage.getItem('i18nextLng') === 'tr' ? 'tr-TR' : 'en-US';

//     const response = await userApiClient.post<ApiResponse>(
//       `/api/identity/users/generate-otp`,
//       { email },
//       {
//         headers: {
//           'Content-Type': 'application/json',
//           'Accept-Language': lang,
//         },
//       }
//     );

//     const data = response.data;

//     // Company servisindeki pattern’e sadık: header varsa kontrol et
//     if (data?.header && data.header.isSuccess === false) {
//       throw new Error(data.header.message || 'OTP oluşturulamadı.');
//     }

//     // Header yoksa (bazı servisler düz payload döndürebilir) direkt iade et
//     return data;
//   } catch (error) {
//     console.error('Error generating OTP:', error);
//     throw new Error('OTP oluşturma sırasında bir hata oluştu.');
//   }
// };

type GenerateOtpRequest = {
  email: string;
};
type GeneratePasswordRequest = {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
};

type GenerateResponse = {
  success: boolean;
  message: string;
};

export const generateOtp = async ({ email }: GenerateOtpRequest): Promise<GenerateResponse> => {
  const payload: GenerateOtpRequest = { email };

  const response = await userApiClient.post<GenerateResponse>(`/users/generate-otp`, payload);

  if (!response.data.success) {
    throw new Error(response.data.message || translate('errors/api:fetch_otp_failed'));
  }

  return response.data;
};

export const generatePassword = async ({
  email,
  otp,
  newPassword,
  confirmPassword,
}: GeneratePasswordRequest): Promise<GenerateResponse> => {
  const payload: GeneratePasswordRequest = { email, otp, newPassword, confirmPassword };

  const response = await userApiClient.post<GenerateResponse>(
    '/users/reset-password-with-otp',
    payload
  );

  if (!response.data?.success) {
    throw new Error(response.data?.message || translate('errors/api:reset_password_failed'));
  }

  return response.data;
};
